import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { AuthProvider } from '@/contexts/AuthContext'
import { initializeSuperTokens } from '@/config/supertokens.config'
import './index.css'
import App from './App.tsx'
import { getLoggerWithPrefix } from './services/shared/LoggerFactory.ts'
import { registerHttpInterceptors } from '@/services/http/interceptor'

// Initialize MSW in development
if (import.meta.env.DEV && (import.meta.env.VITE_ENABLE_MSW === true)) {
  const { worker } = await import('./mocks/browser')
  const logger = getLoggerWithPrefix('main');

  // Check if worker is already started to prevent redundant starts
  if (!worker.listHandlers().length || !document.querySelector('[data-msw-worker]')) {
    await worker.start({
      onUnhandledRequest: 'warn',
      serviceWorker: {
        url: '/mockServiceWorker.js',
      },
      quiet: false, // Set to true to reduce console noise in production
    })
    logger.log('🚀 MSW worker started successfully')
    // Mark that MSW is active
    document.documentElement.setAttribute('data-msw-worker', 'active')
  } else {
    logger.log('🔄 MSW worker already active')
  }
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes
      retry: (failureCount, error: Error & { status?: number }) => {
        // Don't retry on 4xx errors
        if (error?.status && error.status >= 400 && error.status < 500) {
          return false
        }
        return failureCount < 3
      },
    },
    mutations: {
      retry: 1,
    },
  },
})

// Initialize SuperTokens
initializeSuperTokens();
registerHttpInterceptors();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider defaultTheme="system">
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </AuthProvider>
    </ThemeProvider>
  </StrictMode>,
)
